import axios from "axios";

export const paymentEndpoints = (builder) => ({
    getPayments: builder.query({
        query: ({ userGroup, limit }) => ({ url: `/payments?userGroup=${userGroup}&limit=${limit}`, method: 'get' }),
        providesTags: ['Payments'],
    }),
    getWithdrawalRetries: builder.query({
        query: ({ offset = 0, limit = 10 } = {}) => ({
            url: `/payments/withdrawal-retries?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`,
            method: 'get'
        }),
        providesTags: ['Payments'],
    }),
    getFailedWithdrawalsRetryable: builder.query({
        query: ({ offset = 0, limit = 10 } = {}) => ({
            url: `/payments/failed-withdrawals-retryable?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`,
            method: 'get'
        }),
        providesTags: ['Payments'],
    }),
    searchFailedWithdrawals: builder.query({
        query: (search) => ({
            url: `/payments/failed-withdrawal-search/${encodeURIComponent(search || "")}`,
            method: 'get'
        }),
        providesTags: ['Payments'],
    }),
    getPayment: builder.query({
        query: (id) => ({ url: `/payments/${id}`, method: 'get' }),
        providesTags: ['Payments'],
    }),
    getPaymentsSummary: builder.query({
        query: ({ userId, accountId }) => ({ url: `/payments/summary/${userId}/${accountId}`, method: 'get' }),
        providesTags: ['Payments'],
    }),
    updatePayment: builder.mutation({
        query: ({ ref, ...payment }) => ({ url: `/payments/${ref}`, method: 'patch', body: payment }),
        invalidatesTags: ['Payments'],
    }),
    downloadPaymentsReport: builder.mutation({
        query: ({ userGroup, startDate, endDate, type, userId, category, format }) => ({
            url: `/payments/report/${format}?userGroup=${userGroup}`
                + `&startDate=${startDate || ""}`
                + `&endDate=${endDate || ""}`
                + `&type=${type || ""}`
                + `&userId=${userId || ""}`
                + `&category=${category || ""}`,
            method: 'post'
        }),
    }),
    submitPaymentsHistoryExport: builder.mutation({
        query: (filters = {}) => {
            const queryParams = new URLSearchParams();

            Object.entries(filters).forEach(([key, value]) => {
                if (value === undefined || value === null || value === "") {
                    return;
                }

                queryParams.append(key, value);
            });

            const queryString = queryParams.toString();

            return {
                url: `/payments/history/export${queryString ? `?${queryString}` : ""}`,
                method: 'post',
                body: filters,
            };
        },
    }),
    pollPaymentsHistoryExport: builder.mutation({
        async queryFn({ jobId, page = 1, limit = 10000 } = {}) {
            try {
                const response = await axios.get(`/payments/history/export/${jobId}`, {
                    params: { page, limit },
                    validateStatus: () => true,
                });

                if (response.status === 200 || response.status === 202) {
                    return {
                        data: {
                            status: response.status,
                            payload: response.data,
                        },
                    };
                }

                return {
                    error: {
                        status: response.status,
                        data: response.data?.error || response.statusText || "Payments export failed",
                    },
                };
            } catch (error) {
                return {
                    error: {
                        status: error?.response?.status,
                        data: error?.response?.data?.error || error?.message || "Payments export failed",
                    },
                };
            }
        },
    }),
    approveWithdrawal: builder.mutation({
        query: ({ ref, ...withdrawal }) => ({ url: `/payments/approve-withdrawal/${ref}`, method: 'patch', body: withdrawal }),
        invalidatesTags: ['Payments'],
    }),
    approveDeposit: builder.mutation({
        query: ({ ref, ...deposit }) => ({ url: `/payments/approve-deposit/${ref}`, method: 'patch', body: deposit }),
        invalidatesTags: ['Payments'],
    }),
    declineWithdrawal: builder.mutation({
        query: ({ ref, ...withdrawal }) => ({ url: `/payments/decline-withdrawal/${ref}`, method: 'patch', body: withdrawal }),
        providesTags: ['Payments'],
    }),
    addPayment: builder.mutation({
        query: body => ({ url: `/payments/add-payment`, method: 'post', body }),
        providesTags: ['Payments'],
    }),
    retryFailedWithdrawal: builder.mutation({
        query: ({ ref, initiatedBy }) => ({
            url: `/payments/retry-failed-withdrawal/${encodeURIComponent(ref)}?initiatedBy=${encodeURIComponent(initiatedBy || "")}`,
            method: 'patch'
        }),
        invalidatesTags: ['Payments'],
    }),
})
